import { Modal } from '@/components/common/AppModal';
import { Alert, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentType,
  type MouseEvent
} from 'react';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import ConfirmActionModal from '@/app/common/components/ConfirmActionModal';
import formatUtcDate from '@/helpers/formatUtcDate';
import type { ProviderKey } from '@/app/Settings/types/aiSettingsApiTypes';
import SubtleBadge from '@/components/common/SubtleBadge';
import AddAIApiKeyModal from './AddAIApiKeyModal';
import EditAIApiKeyModal from './EditAIApiKeyModal';

type ProviderKeysTableProps = {
  isLoading?: boolean;
  providerKeys: ProviderKey[];
  onToggleApiKeyStatus?: (keyId: string, isActive: boolean) => void;
  onDeleteApiKey?: (keyId: string) => void | Promise<void>;
  isDeletingApiKey?: boolean;
};

type CellParams = {
  data?: ProviderKey;
};

type AgGridWrapperProps = {
  table_name: string;
  height: string;
  columns: unknown;
  data: ProviderKey[];
  total: number;
  hideFooter?: boolean;
  hideExportHelpAlert?: boolean;
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const GRID_SETTINGS = {
  pagination: false,
  fullscreen: false,
  changeHeight: false,
  saveGrid: false,
  resetGrid: false,
  sideBar: false,
  exportBtn: false,
  export: { enabled: false }
};

const ProviderKeysTable = ({
  isLoading,
  providerKeys,
  onToggleApiKeyStatus,
  onDeleteApiKey,
  isDeletingApiKey = false
}: ProviderKeysTableProps) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [keyToEdit, setKeyToEdit] = useState<ProviderKey | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<ProviderKey | null>(null);
  const [helpHtml, setHelpHtml] = useState('');

  const columns = useMemo(
    () => [
      {
        field: 'key_name',
        title: 'نام کلید',
        type: 'string',
        flex: 1,
        minWidth: 120,
        searchable: true
      },
      {
        field: 'provider',
        title: 'ارائه‌دهنده',
        type: 'string',
        flex: 1,
        minWidth: 110,
        searchable: true
      },
      {
        field: 'model',
        title: 'مدل',
        type: 'string',
        flex: 1.2,
        minWidth: 140,
        searchable: true
      },
      {
        field: 'encrypted_api_key',
        title: 'API Key',
        type: 'string',
        flex: 1.4,
        minWidth: 160,
        searchable: true
      },
      {
        field: 'last_used_at',
        title: 'آخرین استفاده',
        type: 'date',
        flex: 1,
        minWidth: 150,
        searchable: false,
        cellRenderer: (params: CellParams) => {
          const value = params.data?.last_used_at;
          if (!value) {
            return <span className="text-secondary">-</span>;
          }
          return (
            <>
              {formatUtcDate(value, { preset: 'dateTime' }) || (
                <span className="text-secondary">-</span>
              )}
            </>
          );
        }
      },
      {
        field: 'description',
        title: 'راهنما',
        type: 'string',
        width: 90,
        maxWidth: 100,
        sortable: false,
        searchable: false,
        noFilter: true,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (params: CellParams) => {
          const description = params.data?.description;
          if (!description) {
            return <span className="text-secondary">-</span>;
          }
          return (
            <div className="d-flex h-100 align-items-center justify-content-center">
              <Button
                variant="link"
                className="p-0"
                aria-label="نمایش راهنما"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setHelpHtml(description);
                }}
              >
                <FontAwesomeIcon icon="question-circle" className="fs-0" />
              </Button>
            </div>
          );
        }
      },
      {
        field: 'verified',
        title: 'وضعیت',
        type: 'string',
        width: 120,
        minWidth: 110,
        searchable: false,
        cellRenderer: (params: CellParams) => {
          const verified = Boolean(params.data?.verified);
          const lastError = params.data?.last_error;
          const label = verified ? 'تأییدشده' : 'تأییدنشده';
          const badgeTitle =
            !verified && lastError ? `${label}: ${lastError}` : label;
          return (
            <div className="d-flex align-items-center h-100">
              <span title={badgeTitle}>
                <SubtleBadge
                  bg={verified ? 'success' : 'warning'}
                  pill
                  className="fs--2 fw-normal"
                  style={{ lineHeight: 1.25 }}
                >
                  {label}
                </SubtleBadge>
              </span>
            </div>
          );
        }
      },
      {
        field: 'is_active',
        title: 'فعال‌سازی',
        type: 'string',
        width: 130,
        minWidth: 120,
        searchable: false,
        cellRenderer: (params: CellParams) => {
          const row = params.data;
          if (!row) return null;
          const statusLabel = row.is_active ? 'فعال' : 'غیرفعال';
          const switchId = `provider-key-status-${row.id}`;
          return (
            <div className="d-flex h-100 align-items-center gap-2">
              <div
                className="form-check form-switch mb-0 p-0"
                style={{ minHeight: 0, display: 'flex', alignItems: 'center' }}
              >
                <input
                  id={switchId}
                  type="checkbox"
                  role="switch"
                  className="form-check-input m-0"
                  checked={Boolean(row.is_active)}
                  aria-label={statusLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    e.stopPropagation();
                    onToggleApiKeyStatus?.(row.id, e.target.checked);
                  }}
                />
              </div>
              <label htmlFor={switchId} className="mb-0 lh-1 user-select-none">
                {statusLabel}
              </label>
            </div>
          );
        }
      },
      {
        field: '_actions',
        title: 'عملیات',
        width: 100,
        maxWidth: 110,
        sortable: false,
        searchable: false,
        noFilter: true,
        suppressHeaderMenuButton: true,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (params: CellParams) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="d-inline-flex align-items-center justify-content-center gap-2 h-100 w-100">
              <Button
                variant="link"
                className="p-0"
                aria-label="ویرایش کلید"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setKeyToEdit(row);
                }}
              >
                <FontAwesomeIcon icon="edit" className="fs--1" />
              </Button>
              <Button
                variant="link"
                className="p-0 text-danger"
                aria-label="حذف کلید"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setKeyToDelete(row);
                }}
              >
                <FontAwesomeIcon icon="trash-alt" className="fs--1" />
              </Button>
            </div>
          );
        }
      }
    ],
    [onToggleApiKeyStatus]
  );

  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: ProviderKey }) => params.data.id,
      rowHeight: 48,
      animateRows: false,
      suppressCellFocus: true,
      pagination: false
    }),
    []
  );

  return (
    <>
      <Card.Body className="pt-0">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="fw-semibold">کلیدهای API ثبت‌شده</div>
          <Button
            size="sm"
            variant="falcon-primary"
            onClick={() => setShowAddModal(true)}
          >
            افزودن API Key
          </Button>
        </div>

        {isLoading ? (
          <div className="text-muted fs--1">در حال دریافت لیست کلیدها...</div>
        ) : providerKeys.length === 0 ? (
          <Alert variant="secondary" className="mb-0 fs--1">
            هنوز کلیدی ثبت نشده است.
          </Alert>
        ) : (
          <div className="ag-grid-in-card">
            <TypedAgGridWrapper
              table_name="settings_ai_provider_keys"
              height="auto"
              columns={columns}
              data={providerKeys}
              total={providerKeys.length}
              hideFooter
              hideExportHelpAlert
              settings={GRID_SETTINGS}
              otherOptions={otherOptions}
            />
          </div>
        )}
      </Card.Body>

      <AddAIApiKeyModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={() => {
          setShowAddModal(false);
        }}
      />

      <EditAIApiKeyModal
        show={Boolean(keyToEdit)}
        keyId={keyToEdit?.id}
        onHide={() => setKeyToEdit(null)}
        onSubmit={() => {
          setKeyToEdit(null);
        }}
      />

      <ConfirmActionModal
        show={Boolean(keyToDelete)}
        onClose={() => setKeyToDelete(null)}
        title="حذف API Key"
        description={
          keyToDelete
            ? `آیا مطمئن هستید می‌خواهید کلید ${keyToDelete.key_name || keyToDelete.provider} حذف شود؟`
            : ''
        }
        confirmText="آره، حذف بشه"
        cancelText="نه"
        confirmVariant="danger"
        onConfirm={async () => {
          if (!keyToDelete) return;
          await onDeleteApiKey?.(keyToDelete.id);
          setKeyToDelete(null);
        }}
        isConfirming={isDeletingApiKey}
      />

      <Modal show={Boolean(helpHtml)} onHide={() => setHelpHtml('')} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-0">راهنمای دریافت</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div dangerouslySetInnerHTML={{ __html: helpHtml }} />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProviderKeysTable;
